<template>
  <div class="kv-ve-settings">
    <div v-if="schema && schema.tabs && schema.tabs.length" class="kv-ve-settings__tabs">
      <button
        v-for="tab in schema.tabs"
        :key="tab.key"
        class="kv-ve-settings__tab"
        :class="{ active: activeTabKey === tab.key }"
        @click="activeTabKey = tab.key"
      >
        {{ tab.title }}
      </button>
    </div>

    <div v-if="activeTab" class="kv-ve-settings__body">
      <div
        v-for="section in activeTab.sections || []"
        :key="section.key || section.title"
        class="kv-ve-settings__section"
      >
        <div v-if="section.title" class="kv-ve-settings__section-title">{{ section.title }}</div>

        <div v-for="field in section.fields || []" :key="field.key || field.label" class="form-group">
          <label v-if="field.label">{{ field.label }}</label>

          <input
            v-if="field.type === 'text'"
            class="form-control"
            :placeholder="field.placeholder || ''"
            :value="getFieldValue(field)"
            @input="setFieldValue(field, $event.target.value)"
          />

          <input
            v-else-if="field.type === 'number'"
            class="form-control"
            type="number"
            :min="field.min"
            :max="field.max"
            :value="getFieldValue(field)"
            @input="setFieldValue(field, toNumberOrNull($event.target.value))"
          />

          <input
            v-else-if="field.type === 'color'"
            class="form-control"
            type="color"
            :value="getFieldValue(field)"
            @input="setFieldValue(field, $event.target.value)"
          />

          <select
            v-else-if="field.type === 'select'"
            class="form-control"
            :value="getFieldValue(field)"
            @change="setFieldValue(field, $event.target.value)"
          >
            <option v-for="opt in field.options || []" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>

          <div v-else-if="field.type === 'toggle'" class="custom-control custom-switch">
            <input
              class="custom-control-input"
              type="checkbox"
              :id="fieldId(field)"
              :checked="Boolean(getFieldValue(field))"
              @change="setFieldValue(field, Boolean($event.target.checked))"
            />
            <label class="custom-control-label" :for="fieldId(field)">{{ field.toggleLabel || "" }}</label>
          </div>

          <small v-if="field.help" class="form-text text-muted">{{ field.help }}</small>
        </div>
      </div>
    </div>

    <div v-else class="text-muted">{{ t('visual_editor-no_settings') }}</div>
  </div>
</template>

<script>
function getPath(obj, path) {
  if (!path) return undefined;
  const parts = String(path).split(".");
  let cur = obj;
  for (const p of parts) {
    if (!cur) return undefined;
    cur = cur[p];
  }
  return cur;
}

function ensurePath(vm, obj, parts) {
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const p = parts[i];
    if (cur[p] === undefined || cur[p] === null || typeof cur[p] !== "object") {
      vm.$set(cur, p, {});
    }
    cur = cur[p];
  }
  return cur;
}

export default {
  name: "VeSettingsPanel",
  props: {
    schema: { type: Object, default: null },
    target: { type: Object, default: null },
    ctx: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      activeTabKey: null,
    };
  },
  computed: {
    activeTab() {
      const tabs = this.schema?.tabs || [];
      const key = this.activeTabKey || (tabs[0] && tabs[0].key);
      return tabs.find((t) => t.key === key) || null;
    },
  },
  watch: {
    schema: {
      immediate: true,
      deep: true,
      handler(next) {
        const tabs = next?.tabs || [];
        if (!tabs.length) {
          this.activeTabKey = null;
          return;
        }
        if (!this.activeTabKey || !tabs.find((t) => t.key === this.activeTabKey)) {
          this.activeTabKey = tabs[0].key;
        }
      },
    },
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    fieldId(field) {
      return `kv-ve-field-${field.key || field.label || "x"}`;
    },
    toNumberOrNull(v) {
      if (v === "" || v === null || v === undefined) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    },
    getFieldValue(field) {
      if (!this.target) return undefined;
      if (typeof field.get === "function") return field.get(this.target, this.ctx);
      if (field.path) return getPath(this.target, field.path);
      return undefined;
    },
    setFieldValue(field, value) {
      if (!this.target) return;
      if (typeof field.set === "function") {
        field.set(this.target, value, this.ctx);
        return;
      }
      if (!field.path) return;
      const parts = String(field.path).split(".");
      const base = ensurePath(this, this.target, parts);
      this.$set(base, parts[parts.length - 1], value);
    },
  },
};
</script>

<style scoped lang="less">
.kv-ve-settings__tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}
.kv-ve-settings__tab {
  flex: 1;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  border-radius: 12px;
  padding: 10px 12px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  color: #344054;
  transition: box-shadow 0.15s ease, border-color 0.15s ease, background 0.15s ease;
}
.kv-ve-settings__tab.active {
  background: rgba(42, 170, 76, 0.10);
  border-color: rgba(42, 170, 76, 0.45);
  color: #00542e;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);
}
.kv-ve-settings__section + .kv-ve-settings__section {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #eef2f6;
}
.kv-ve-settings__section-title {
  font-weight: 700;
  font-size: 12px;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 10px;
}
</style>


